import React from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import cn from 'classnames';
import { format, isValid, parse } from 'date-fns';

import DatepickerWrapper from '../../components/Datepicker/DatepickerWrapper';
import { DebouncedTextInput } from '../../components/CustomDashboard/DebouncedTextInput';
import {
  singleSelectComponents,
  useMultiSelectComponents,
} from '../../components/CustomDashboard/multiSelectComponents';
import { ArtifactArchived, ArtifactSorting, ArtifactType } from '../../utils/artifactsApi';
import { ARCHIVED_OPTIONS, ARTIFACT_TYPE_LABELS, SORTING_OPTIONS } from '../../utils/artifactsUi';
import styles from './ArtifactsPage.module.scss';
import '../../components/CustomDashboard/wf-select-portal.scss';

const Datepicker = DatepickerWrapper as unknown as React.FC<{
  onChange: (start: Date | null, end: Date | null) => void;
  initStartDate: Date | null;
  initEndDate: Date | null;
  isRange: boolean;
  className?: string;
  emptyLabel?: string;
  clearable?: boolean;
}>;

const SEARCH_DEBOUNCE_MS = 400;

interface ArtifactFiltersBarProps {
  q: string;
  types: ArtifactType[];
  dateFrom: string;
  dateTo: string;
  sorting: ArtifactSorting;
  archived: ArtifactArchived;
  showArchivedFilter: boolean;
  onSearch: (value: string) => void;
  onTypes: (values: ArtifactType[]) => void;
  onDates: (from: string, to: string) => void;
  onSorting: (value: ArtifactSorting) => void;
  onArchived: (value: ArtifactArchived) => void;
  children?: React.ReactNode;
}

function parseDateParam(raw: string): Date | null {
  if (!raw) return null;
  const parsed = parse(raw, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
}

const selectPortalProps: Record<string, any> = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  styles: { menuPortal: (base: object) => ({ ...base, zIndex: 9999 }) },
};

export function ArtifactFiltersBar({
  q,
  types,
  dateFrom,
  dateTo,
  sorting,
  archived,
  showArchivedFilter,
  onSearch,
  onTypes,
  onDates,
  onSorting,
  onArchived,
  children,
}: ArtifactFiltersBarProps) {
  const { t } = useTranslation();
  const multiSelectComponents = useMultiSelectComponents();

  const typeOptions = Object.entries(ARTIFACT_TYPE_LABELS).map(([value, label]) => ({
    value: value as ArtifactType,
    label: t(label),
    stringValue: value,
  }));
  const sortingOptions = SORTING_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));
  const archivedOptions = ARCHIVED_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));

  return (
    <div className={styles.filters}>
      {children}
      <div className={styles.filterItem}>
        <span className={styles.filterLabel}>{t('Search')}</span>
        <DebouncedTextInput
          className={styles.textInput}
          value={q}
          delay={SEARCH_DEBOUNCE_MS}
          placeholder={t('Search by name') as string}
          ariaLabel={t('Search') as string}
          onChange={(value) => onSearch((value ?? '').trim())}
        />
      </div>
      <div className={styles.filterItem}>
        <span className={styles.filterLabel}>{t('Type')}</span>
        <Select
          isMulti
          aria-label={t('Type') as string}
          className="wf-select-container"
          classNamePrefix="wf-select"
          placeholder={t('All')}
          options={typeOptions}
          value={typeOptions.filter((option) => types.includes(option.value))}
          onChange={(selected) => onTypes(((selected ?? []) as { value: ArtifactType }[]).map((o) => o.value))}
          components={multiSelectComponents}
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          isSearchable={false}
          {...selectPortalProps}
        />
      </div>
      <div className={cn(styles.filterItem, styles.filterItemDate)}>
        <span className={styles.filterLabel}>{t('Date')}</span>
        <Datepicker
          className={styles.datepicker}
          isRange
          clearable
          initStartDate={parseDateParam(dateFrom)}
          initEndDate={parseDateParam(dateTo)}
          onChange={(start, end) =>
            onDates(start ? format(start, 'yyyy-MM-dd') : '', end ? format(end, 'yyyy-MM-dd') : '')
          }
        />
      </div>
      <div className={styles.filterItem}>
        <span className={styles.filterLabel}>{t('Sorting')}</span>
        <Select
          aria-label={t('Sorting') as string}
          className="wf-select-container"
          classNamePrefix="wf-select"
          options={sortingOptions}
          value={sortingOptions.find((option) => option.value === sorting) ?? sortingOptions[0]}
          onChange={(selected) => selected && onSorting((selected as { value: ArtifactSorting }).value)}
          components={singleSelectComponents}
          isSearchable={false}
          {...selectPortalProps}
        />
      </div>
      {showArchivedFilter && (
        <div className={styles.filterItem}>
          <span className={styles.filterLabel}>{t('Archive')}</span>
          <Select
            aria-label={t('Archive') as string}
            className="wf-select-container"
            classNamePrefix="wf-select"
            options={archivedOptions}
            value={archivedOptions.find((option) => option.value === archived) ?? archivedOptions[0]}
            onChange={(selected) => selected && onArchived((selected as { value: ArtifactArchived }).value)}
            components={singleSelectComponents}
            isSearchable={false}
            {...selectPortalProps}
          />
        </div>
      )}
    </div>
  );
}
